import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { getMyRfq, submitMyQuotation } from "@/lib/vendor-portal.functions";
import { DynamicFieldsRenderer } from "@/features/rfqs/DynamicFieldsRenderer";
import type { TemplateField } from "@/features/quotation-templates/types";

export const Route = createFileRoute("/vendor/rfqs/$rfqId")({
  component: RfqDetail,
});

function RfqDetail() {
  const { rfqId } = Route.useParams() as { rfqId: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fn = useServerFn(getMyRfq);
  const submit = useServerFn(submitMyQuotation);

  const { data, isLoading } = useQuery({
    queryKey: ["vendor", "rfq", rfqId],
    queryFn: () => fn({ data: { rfq_id: rfqId } }),
  });

  const [lines, setLines] = useState<Record<string, { unit_price: string; tax_pct: string; delivery_days: string; remarks: string; custom_values: Record<string, unknown> }>>({});
  const [remarks, setRemarks] = useState("");
  const [headerCustom, setHeaderCustom] = useState<Record<string, unknown>>({});

  const templateFields: TemplateField[] = useMemo(() => {
    const dbFields = ((data as any)?.templateFields ?? []) as any[];
    if (dbFields.length) {
      return dbFields.map((f) => ({
        field_key: f.field_key,
        label: f.label,
        field_type: f.field_type,
        options_json: f.options_json ?? null,
        required: f.required,
        applies_to: f.applies_to,
        sort_order: f.sort_order,
      }));
    }
    // fallback: RFQ's inline custom_fields_schema
    const inline = ((data as any)?.rfq?.custom_fields_schema ?? []) as TemplateField[];
    return Array.isArray(inline) ? inline : [];
  }, [data]);

  useEffect(() => {
    if (!data) return;
    const q = (data.quotation as any);
    setRemarks(q?.remarks ?? "");
    setHeaderCustom((q?.custom_values as Record<string, unknown>) ?? {});
    const map: any = {};
    for (const it of data.items) {
      const existing = data.quotationItems.find((qi: any) => qi.rfq_item_id === it.id);
      map[it.id] = {
        unit_price: existing?.unit_price?.toString() ?? "",
        tax_pct: existing?.tax_pct?.toString() ?? "0",
        delivery_days: existing?.delivery_days?.toString() ?? "",
        remarks: existing?.remarks ?? "",
        custom_values: (existing?.custom_values as Record<string, unknown>) ?? {},
      };
    }
    setLines(map);
  }, [data]);

  const total = useMemo(() => {
    let sum = 0;
    for (const it of data?.items ?? []) {
      const l = lines[it.id];
      if (!l) continue;
      const price = Number(l.unit_price) || 0;
      const tax = Number(l.tax_pct) || 0;
      const qty = Number((it as any).qty ?? (it as any).quantity ?? 1) || 1;
      sum += price * qty * (1 + tax / 100);
    }
    return sum;
  }, [lines, data]);

  const save = useMutation({
    mutationFn: async () => {
      if (!data) return;
      const items = data.items.map((it: any) => {
        const l = lines[it.id];
        const unit_price = Number(l?.unit_price) || 0;
        const tax_pct = Number(l?.tax_pct) || 0;
        return {
          rfq_item_id: it.id,
          unit_price,
          tax_pct,
          total_price: unit_price * (1 + tax_pct / 100),
          delivery_days: l?.delivery_days ? Number(l.delivery_days) : null,
          remarks: l?.remarks || null,
          custom_values: l?.custom_values ?? {},
        };
      }).filter((r: any) => r.unit_price > 0);
      await submit({ data: { rfq_id: rfqId, remarks, custom_values: headerCustom, items } });
    },
    onSuccess: () => {
      toast.success("Quotation submitted");
      qc.invalidateQueries({ queryKey: ["vendor"] });
      navigate({ to: ("/vendor/rfqs" as any) });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <p>Loading…</p>;
  if (!data?.rfq) return <p>RFQ not found.</p>;
  const rfq = data.rfq as any;
  const template = rfq.template;
  const hasLineCustom = templateFields.some((f) => f.applies_to === "line_item");
  const hasHeaderCustom = templateFields.some((f) => f.applies_to === "header");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{rfq.rfq_no ?? "RFQ"}</h1>
        <p className="text-sm text-muted-foreground">Due: {rfq.due_date ?? "—"}</p>
        {template?.name && (
          <p className="mt-1 text-xs text-muted-foreground">Template: <span className="font-medium">{template.name}</span>{template.description ? ` — ${template.description}` : ""}</p>
        )}
      </div>

      {/* Buyer-provided terms */}
      {(rfq.terms_and_conditions || rfq.payment_terms || rfq.delivery_terms || template) && (
        <div className="rounded-md border bg-muted/30 p-4 space-y-2">
          <h3 className="text-sm font-semibold">Buyer Terms</h3>
          <div className="grid gap-2 text-sm md:grid-cols-2">
            {rfq.payment_terms && <div><span className="text-xs text-muted-foreground">Payment Terms</span><div className="whitespace-pre-wrap">{rfq.payment_terms}</div></div>}
            {rfq.delivery_terms && <div><span className="text-xs text-muted-foreground">Delivery Terms</span><div className="whitespace-pre-wrap">{rfq.delivery_terms}</div></div>}
            {rfq.terms_and_conditions && <div className="md:col-span-2"><span className="text-xs text-muted-foreground">Terms &amp; Conditions</span><div className="whitespace-pre-wrap">{rfq.terms_and_conditions}</div></div>}
          </div>
        </div>
      )}

      <div className="rounded-md border bg-card">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/40 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">#</th>
              <th className="px-3 py-2 text-left">Item</th>
              <th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-right">Unit Price ₹</th>
              <th className="px-3 py-2 text-right">Tax %</th>
              <th className="px-3 py-2 text-right">Delivery (days)</th>
              <th className="px-3 py-2 text-left">Remarks</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((it: any, idx: number) => (
              <>
                <tr key={it.id} className="border-b last:border-0">
                  <td className="px-3 py-2">{idx + 1}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{it.item_name}</div>
                    {it.description && <div className="text-xs text-muted-foreground">{it.description}</div>}
                    {it.spec && <div className="text-xs text-muted-foreground">Spec: {it.spec}</div>}
                  </td>
                  <td className="px-3 py-2 text-right">{Number(it.qty ?? it.quantity ?? 1)}</td>
                  <td className="px-3 py-2">
                    <Input type="number" step="0.01" className="h-8 text-right"
                      value={lines[it.id]?.unit_price ?? ""}
                      onChange={(e) => setLines((l) => ({ ...l, [it.id]: { ...l[it.id], unit_price: e.target.value } }))}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input type="number" step="0.01" className="h-8 text-right"
                      value={lines[it.id]?.tax_pct ?? "0"}
                      onChange={(e) => setLines((l) => ({ ...l, [it.id]: { ...l[it.id], tax_pct: e.target.value } }))}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input type="number" className="h-8 text-right"
                      value={lines[it.id]?.delivery_days ?? ""}
                      onChange={(e) => setLines((l) => ({ ...l, [it.id]: { ...l[it.id], delivery_days: e.target.value } }))}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input className="h-8"
                      value={lines[it.id]?.remarks ?? ""}
                      onChange={(e) => setLines((l) => ({ ...l, [it.id]: { ...l[it.id], remarks: e.target.value } }))}
                    />
                  </td>
                </tr>
                {hasLineCustom && (
                  <tr key={`${it.id}-custom`} className="border-b last:border-0 bg-muted/20">
                    <td colSpan={7} className="px-3 py-3">
                      <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">Line details (as per template)</div>
                      <DynamicFieldsRenderer
                        fields={templateFields}
                        values={lines[it.id]?.custom_values ?? {}}
                        onChange={(cv) => setLines((l) => ({ ...l, [it.id]: { ...l[it.id], custom_values: cv } }))}
                        scope="line_item"
                        compact
                      />
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {hasHeaderCustom && (
        <div className="rounded-md border p-4 space-y-2">
          <h3 className="text-sm font-semibold">Header details (as per template)</h3>
          <DynamicFieldsRenderer
            fields={templateFields}
            values={headerCustom}
            onChange={setHeaderCustom}
            scope="header"
          />
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs font-semibold uppercase text-muted-foreground">Overall remarks / T&amp;C</Label>
          <Textarea rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        </div>
        <div className="flex flex-col items-end justify-end">
          <p className="text-xs text-muted-foreground">Estimated total</p>
          <p className="text-3xl font-semibold">₹{total.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate({ to: ("/vendor/rfqs" as any) })}>Cancel</Button>
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? "Submitting…" : (data.quotation ? "Update Quotation" : "Submit Quotation")}
        </Button>
      </div>
    </div>
  );
}
